import { useState } from 'react';
import type { ReactNode } from 'react';
import IntersectionObserver from '@researchgate/react-intersection-observer';

import { useTheme, Grid, Text } from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useIsMobileScreen } from '@/hooks/useMedia';
import Tooltip from '@/components/Tooltip';

import {
  ValuationColumnHeaderTooltip,
  GrowthColumnHeaderTooltip,
  AnalystTargetColumnHeaderTooltip,
  MyFairValueTooltip,
} from '../Tooltips';

import {
  StyledTable,
  TableHeadRow,
  TableHeadCell,
  InformationIcon,
  ScrollPane,
} from './styled';

interface Props {
  children?: ReactNode;
  itemId?: string | null;
}

export const Table = ({ itemId, children }: Props) => {
  const theme = useTheme();
  const [isSticky, setIsSticky] = useState(false);
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  if (isMobileScreen) {
    return (
      <StyledTable>
        <thead data-testid="table-head">
          <TableHeadRow>
            <TableHeadCell>
              <Grid templateColumns="1fr 1fr" justifyContent="space-between">
                <Text typography="tiny" color="textSofter" textAlign="left">
                  Company
                </Text>
                <Text typography="tiny" color="textSofter" textAlign="right">
                  Price & Valuation
                </Text>
              </Grid>
            </TableHeadCell>
          </TableHeadRow>
        </thead>
        <tbody data-testid="table-body">{children}</tbody>
      </StyledTable>
    );
  }

  return (
    <ScrollPane>
      <StyledTable>
        <thead data-testid="table-head">
          <TableHeadRow>
            <IntersectionObserver
              threshold={[1]}
              onChange={({ intersectionRatio }) => {
                setIsSticky(intersectionRatio < 1);
              }}
            >
              <TableHeadCell isSticky={isSticky}>Company</TableHeadCell>
            </IntersectionObserver>
            <TableHeadCell>Price & Valuation</TableHeadCell>
            <TableHeadCell>
              <Grid
                templateColumns="min-content min-content"
                alignItems="center"
                gap="4px"
                paddingRight="x2"
              >
                My FV & Narrative{' '}
                <Tooltip
                  title={<MyFairValueTooltip />}
                  light
                  placement="top"
                  zIndex={99999}
                >
                  <Grid>
                    <InformationIcon
                      fill={theme.color.whiteRGBA(0.5)}
                      aria-label="About my fair value"
                    />
                  </Grid>
                </Tooltip>
              </Grid>
            </TableHeadCell>
            {!itemId ? (
              <>
                <TableHeadCell>
                  <Grid
                    templateColumns="min-content min-content"
                    alignItems="center"
                    gap="4px"
                    paddingRight="x2"
                  >
                    Valuation{' '}
                    <Tooltip
                      title={<ValuationColumnHeaderTooltip />}
                      light
                      placement="top"
                      zIndex={99999}
                    >
                      <Grid>
                        <InformationIcon
                          fill={theme.color.whiteRGBA(0.5)}
                          aria-label="About the valuation column"
                        />
                      </Grid>
                    </Tooltip>
                  </Grid>
                </TableHeadCell>
                <TableHeadCell>
                  <Grid
                    templateColumns="min-content min-content"
                    alignItems="center"
                    gap="4px"
                    paddingRight="x2"
                  >
                    Growth{' '}
                    <Tooltip
                      title={<GrowthColumnHeaderTooltip />}
                      light
                      placement="top"
                      zIndex={99999}
                    >
                      <Grid>
                        <InformationIcon
                          fill={theme.color.whiteRGBA(0.5)}
                          aria-label="About the growth column"
                        />
                      </Grid>
                    </Tooltip>
                  </Grid>
                </TableHeadCell>
                <TableHeadCell>
                  <Grid
                    templateColumns="min-content min-content"
                    alignItems="center"
                    gap="4px"
                    paddingRight="x2"
                  >
                    Analyst Target{' '}
                    <Tooltip
                      title={<AnalystTargetColumnHeaderTooltip />}
                      light
                      placement="top"
                      zIndex={99999}
                    >
                      <Grid>
                        <InformationIcon
                          fill={theme.color.whiteRGBA(0.5)}
                          aria-label="About the analyst target column"
                        />
                      </Grid>
                    </Tooltip>
                  </Grid>
                </TableHeadCell>
              </>
            ) : null}
            <TableHeadCell />
          </TableHeadRow>
        </thead>
        <tbody data-testid="table-body">{children}</tbody>
      </StyledTable>
    </ScrollPane>
  );
};
